import { type EditAction } from '@/application';
import { evaluate, type EntitySource, type Evaluated, parentsOf } from '@/domain/construction';
import { type Vec2, vec } from '@/domain/math';
import {
  createEntity,
  type EntityId,
  type EntityKind,
  isPlaneSource,
  solidBehind,
  type Style,
  toneForPlane,
} from '@/domain/model';
import { hitTestAll } from '@/engine';

import {
  type PointerInput,
  type Tool,
  type ToolContext,
  type ToolPrompt,
  type ToolResponse,
} from './Tool';

/**
 * Builds a construction from entities the user picks.
 *
 * Every construction tool has the same shape — pick N things, derive one or more entities
 * from them — so it exists once here rather than twenty-five times in the registry. What
 * differs per tool is two functions: which kinds each step accepts, and what to build.
 *
 * ## Why acceptance is per step, not per tool
 *
 * The first version filtered by a single predicate for the whole tool. That is fine for
 * "pick three points" and wrong for almost everything else: a perpendicular takes a point
 * *and* a line, and a shared filter would happily let the user pick two lines and then
 * build nothing. Per-step filters make the illegal pick unclickable instead of letting it
 * fail silently two clicks later.
 *
 * ## Why it previews the real construction
 *
 * Hovering evaluates the construction that *would* be created and draws it. This is free —
 * `evaluate` is pure and the parents are already in the scene — and it turns a tool that
 * demands the user predict the result into one where they can see it.
 *
 * ## Only what evaluates is created
 *
 * The preview and the click run the *same* resolution, and both drop sources that evaluate
 * to nothing. So a tangent from a point inside the circle previews as nothing and creates
 * nothing, rather than adding an entity that is invisible until the point is dragged
 * outside. The guarantee this buys is worth more than the flexibility it costs: what you
 * see under the cursor is exactly what the click produces.
 *
 * This is also how tools with a variable yield work. "Intersect" offers both branch
 * indices and keeps whichever exist, so a line meeting a circle twice creates two points
 * and a tangent line creates one, from the same declaration.
 */

export interface PickStep {
  readonly accepts: (kind: EntityKind) => boolean;
  /**
   * Turns what was clicked into what the construction should refer to.
   *
   * Most steps do not need this: clicking a segment means that segment. A polygon is the
   * exception that forces it to exist — it is *one* entity whose sides are not entities at
   * all, so "the side under the cursor" cannot be named by an id alone. The resolver reads
   * the click position and substitutes a corner point, which is a thing the construction can
   * hold on to and which keeps following the polygon when it is reshaped.
   *
   * Returning `null` refuses the pick, exactly as a failed `accepts` would.
   */
  readonly resolve?: (
    context: ToolContext,
    id: EntityId,
    world: Vec2,
    picked: readonly EntityId[]
  ) => EntityId | null;
  /**
   * Shown to the user as the prompt for this step.
   *
   * In Vietnamese, like the rest of the interface. These are the only user-facing strings in
   * the input layer, and they were invisible until the prompt strip existed — which is how
   * they stayed in English while everything around them was translated.
   */
  readonly hint: string;
}

/**
 * What to build once every step is picked. `null` when the picks cannot form anything.
 *
 * The context is there for the constructions that need to know something about the picks beyond
 * their names — a plane has to find which solid its three corners belong to, because a teacher
 * says "mặt phẳng (SAC)" and never says which figure. Almost every tool ignores it.
 */
export type BuildConstruction = (
  ids: readonly EntityId[],
  context: ToolContext
) => EntitySource | readonly EntitySource[] | null;

/**
 * Why a set of picks builds nothing.
 *
 * A plain string for almost every tool, because there is one way to be wrong and one thing to
 * say. A **function** for the tools where the same refusal has genuinely different causes and a
 * single sentence would have to cover all of them badly.
 *
 * Turning a plane is the case that forced it. Three things end in nothing there — the axis is not
 * in the plane, the turn carries the plane out of the body, or the solid is round and the section
 * would be a conic this editor cannot express — and they need different answers. "Không dựng được
 * với lựa chọn này" is true of all three and useful for none: it tells a teacher their clicks were
 * wrong when in two of the three cases the clicks were right and the *number* wants changing.
 */
export type Refusal = string | ((ids: readonly EntityId[], context: ToolContext) => string);

type PickState =
  { readonly name: 'idle' } | { readonly name: 'picked'; readonly ids: readonly EntityId[] };

export class ConstructionTool implements Tool {
  private state: PickState = { name: 'idle' };

  readonly id: string;
  readonly label: string;
  readonly shortcut: string;
  readonly group: string;
  private readonly prefix: string;
  private readonly steps: readonly PickStep[];
  private readonly build: BuildConstruction;
  private readonly refusal: Refusal;

  constructor(
    id: string,
    label: string,
    shortcut: string,
    group: string,
    prefix: string,
    steps: readonly PickStep[],
    build: BuildConstruction,
    refusal: Refusal | undefined = 'không dựng được với lựa chọn này'
  ) {
    this.refusal = refusal;
    this.id = id;
    this.label = label;
    this.shortcut = shortcut;
    this.group = group;
    this.prefix = prefix;
    this.steps = steps;
    this.build = build;
  }

  /**
   * What this tool wants next.
   *
   * Read by the shell on every emit, so the strip updates as picks accumulate. Returning the
   * *count* as well as the text is what turns "click a point" into "2 of 3" — without it the
   * user cannot tell a tool that is waiting from one that is ignoring them.
   */
  prompt(): ToolPrompt | null {
    const picked = this.picks().length;
    const step = this.steps[picked];
    if (step === undefined) return null;
    return {
      tool: this.label,
      step: picked,
      steps: this.steps.length,
      hint: step.hint,
      ...(this.problem === null ? {} : { problem: this.problem }),
    };
  }

  /**
   * Why the last hovered candidate was refused, if it was.
   *
   * Held on the tool rather than returned in the response because it belongs to the *prompt*,
   * which the shell reads separately — and because it has to survive the emit that follows
   * the pointer move.
   */
  private problem: string | null = null;

  /** Whether the last refusal was "there is something here, but not of a kind I take". */
  private wrongKind = false;

  onPointerDown(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;

    const hit = this.hitForCurrentStep(context, input);
    if (hit === null) {
      /*
       * A click on something this step cannot use has to say so, exactly as hovering does.
       *
       * The refusal used to be set only by `onPointerMove`, so a click that landed without the
       * pointer having moved over the candidate first — which is most clicks, on a touchpad or
       * after a keyboard nudge — produced nothing and explained nothing. That was fixed once for
       * the *last* step and reintroduced here the moment a step began refusing picks of its own:
       * clicking a free point with the plane tool did nothing at all, and nothing said why.
       *
       * Empty space stays silent. It is the ordinary state between picks, and a strip that turns
       * red whenever the pointer is over the page would be red for most of the gesture.
       */
      const step = this.steps[this.picks().length];
      if (this.wrongKind && step !== undefined) this.problem = `ở đây cần ${step.hint}`;
      return null;
    }

    const picked = this.picks().concat(hit);

    if (picked.length < this.steps.length) {
      this.state = { name: 'picked', ids: picked };
      return { selection: picked, preview: [] };
    }

    const resolved = this.resolve(context, picked);

    /*
     * A last pick that builds nothing leaves the earlier picks alone.
     *
     * Discarding them was the first behaviour and it was the worst of both worlds: the click
     * produced nothing *and* silently threw away two correct picks, so the user was returned
     * to the first step with no idea why. Keeping them means the next click is simply another
     * attempt at the same step, which is what a person is already trying to do.
     *
     * And it says why. The refusal used to be set only by `onPointerMove`, so a click that
     * landed without the pointer having moved over the candidate first — which is most clicks,
     * on a touchpad or after a keyboard nudge — produced nothing and explained nothing: the
     * strip still read "Nhấp vào điểm thứ ba" while the user had just clicked a third point.
     * Reported as exactly that.
     */
    if (resolved.length === 0) {
      this.problem = this.why(picked, context);
      return { selection: picked, preview: [] };
    }

    this.state = { name: 'idle' };

    const created: EntityId[] = [];
    const actions: EditAction[] = [];

    for (const { source } of resolved) {
      const id = context.nextId(this.prefix);
      created.push(id);
      actions.push({
        kind: 'add-entity',
        entity: createEntity(id, source, styleFor(source, context.style, context)),
      });
    }

    return { actions, selection: created, preview: [] };
  }

  onPointerMove(context: ToolContext, input: PointerInput): ToolResponse | null {
    const hit = this.hitForCurrentStep(context, input);
    if (hit === null) {
      /*
       * Empty space is not a refusal — it is the ordinary state between picks, and colouring
       * the strip red there would leave it red for most of the gesture. Something present but
       * unusable *is* a refusal, and one the user cannot otherwise account for.
       */
      const step = this.steps[this.picks().length];
      this.problem = this.wrongKind && step !== undefined ? `ở đây cần ${step.hint}` : null;
      return { cursor: 'not-allowed', preview: [] };
    }

    const ids = this.picks().concat(hit);

    // A partial pick previews nothing: there is no meaningful half of a perpendicular, and
    // inventing one would show geometry the next click does not produce.
    if (ids.length < this.steps.length) {
      this.problem = null;
      return { cursor: 'copy', preview: [] };
    }

    /*
     * On the last step the cursor reports whether the click will *do* something, not merely
     * whether the kind under it is acceptable.
     *
     * The two came apart badly. A side of the right kind that does not meet the vertex
     * cannot form an angle, so the cursor said "yes" and the click did nothing — and a
     * cursor that promises what the click cannot deliver is worse than one that refuses.
     */
    const resolved = this.resolve(context, ids);
    this.problem = resolved.length === 0 ? this.why(ids, context) : null;

    return {
      cursor: resolved.length === 0 ? 'not-allowed' : 'copy',
      preview: resolved.map(({ value }) => value),
    };
  }

  cancel(): ToolResponse | null {
    this.problem = null;
    if (this.state.name === 'idle') return null;
    this.state = { name: 'idle' };
    return { selection: [], preview: [] };
  }

  reset(): void {
    this.state = { name: 'idle' };
    this.problem = null;
  }

  /** The refusal for this particular set of picks. */
  private why(ids: readonly EntityId[], context: ToolContext): string {
    return typeof this.refusal === 'string' ? this.refusal : this.refusal(ids, context);
  }

  private picks(): EntityId[] {
    return this.state.name === 'idle' ? [] : [...this.state.ids];
  }

  /**
   * The entity under the pointer, if the *current* step accepts its kind.
   *
   * Re-picking something already chosen is refused. Without that, "perpendicular bisector
   * of A and A" is two clicks away and yields a construction that is permanently
   * undefined — which reads to the user as the tool being broken.
   */
  private hitForCurrentStep(context: ToolContext, input: PointerInput): EntityId | null {
    const picked = this.picks();
    const step = this.steps[picked.length];
    if (step === undefined) return null;

    /*
     * One sweep, filtered afterwards, rather than one sweep with `accepts` inside the filter.
     *
     * The difference is that "nothing here" and "something here this step cannot use" become
     * distinguishable, and only the second deserves an explanation. Filtering inside the
     * sweep collapses them, which is how a polygon side managed to refuse in total silence:
     * the tool never learned there had been anything under the cursor at all.
     */
    const under = hitTestAll(context.scene, input.world, context.tolerance, {
      filter: (id) => !picked.includes(id),
    });

    const candidates = under
      .filter((hit) => step.accepts(hit.kind))
      .flatMap((hit) => {
        const id =
          step.resolve === undefined ? hit.id : step.resolve(context, hit.id, input.world, picked);
        return id === null || picked.includes(id) ? [] : [{ ...hit, id }];
      });

    if (candidates.length === 0) {
      this.wrongKind = under.length > 0;
      return null;
    }
    this.wrongKind = false;

    /*
     * On the final step, prefer a candidate the construction can actually use.
     *
     * Objects overlap — near the vertex of a triangle three sides and a point are all within
     * a few pixels. Taking only the top-priority hit meant that if *that* one could not
     * complete the construction, the click failed even though another candidate under the
     * cursor would have worked. The user experiences that as "the tool refuses my second
     * side", with no way to tell which of the overlapping things it was objecting to.
     */
    if (picked.length + 1 === this.steps.length) {
      const usable = candidates.find(
        (candidate) => this.resolve(context, [...picked, candidate.id]).length > 0
      );
      if (usable !== undefined) return usable.id;
    }

    return candidates[0]?.id ?? null;
  }

  /**
   * The sources that would be created, with what each evaluates to.
   *
   * The single source of truth for both the preview and the click, which is what keeps
   * them from disagreeing.
   */
  private resolve(
    context: ToolContext,
    ids: readonly EntityId[]
  ): readonly { source: EntitySource; value: Evaluated }[] {
    const built = this.build(ids, context);
    if (built === null) return [];
    const sources: readonly EntitySource[] = 'op' in built ? [built] : built;

    const resolved: { source: EntitySource; value: Evaluated }[] = [];
    for (const source of sources) {
      const parents = parentsOf(source).map((id) => context.scene.values.get(id) ?? null);
      const value = evaluate(source, parents);
      if (value !== null) resolved.push({ source, value });
    }
    return resolved;
  }
}

/* ------------------------------------------------------------------ step helpers */

/** Steps are declared with these rather than inline lambdas, so tools read as prose. */
export const aPoint = (hint = 'một điểm'): PickStep => ({
  accepts: (kind) => kind === 'point',
  hint,
});

/**
 * A point that belongs to a solid — a corner, or anything built from corners.
 *
 * ## The loop this breaks
 *
 * *"khi dựng mặt phẳng nghiêng thì đòi chọn mặt phẳng! khi dựng mặt phẳng thì đòi chọn 3 điểm,
 * khi chọn 3 điểm thì không cho chọn mặt phẳng"* — three tools, each pointing at the next, and no
 * way in. On a canvas with no solid every one of them refuses, and the refusals read as a circle.
 *
 * The circle is real but the wall is not where it looked. A plane here is a **section**, so it
 * needs a body to cut, and three marks on a page name no plane in space — that part is right and
 * stays. What was wrong is that `aPoint` accepted the first two clicks with an inviting cursor and
 * only refused on the third: the tool actively walked the user down a path it was always going to
 * block, and then blamed the last click.
 *
 * Now the very first hover over a free point says so. That is three clicks saved and, more to the
 * point, the step prompt itself reads *"một đỉnh của hình khối"* before anything is clicked — so
 * the requirement announces itself instead of being discovered.
 *
 * ## Why `resolve` and not a new hook
 *
 * `resolve` already refuses a pick by returning `null`, and `hitForCurrentStep` already
 * distinguishes "nothing here" from "something here this step cannot use" — which is what turns
 * this into *"ở đây cần một đỉnh của hình khối"* rather than a silent cursor. No new machinery.
 *
 * A point **built on** corners qualifies: `solidBehind` walks the parents, so the midpoint of two
 * corners, a foot, or an intersection of two edges all answer. That is the whole reason it exists,
 * and refusing them would rule out most of the figures a problem actually names.
 */
export const aSolidPoint = (hint = 'một đỉnh của hình khối'): PickStep => ({
  accepts: (kind) => kind === 'point',
  hint,
  resolve: (context, id) => (solidBehind(context.document, id) === null ? null : id),
});

export const aLinear = (hint = 'một đường thẳng, tia hoặc đoạn'): PickStep => ({
  accepts: (kind) => kind === 'line' || kind === 'ray' || kind === 'segment',
  hint,
});

export const aCircle = (hint = 'một đường tròn'): PickStep => ({
  accepts: (kind) => kind === 'circle',
  hint,
});

export const aSegment = (hint = 'một đoạn thẳng'): PickStep => ({
  accepts: (kind) => kind === 'segment',
  hint,
});

/** An existing plane, for the constructions that take one — the giao tuyến of two. */
/**
 * The style a new construction arrives with.
 *
 * The pending style for everything, except that a **plane** arrives toned. A plane drawn as a
 * bare outline cannot be told from three more edges of the solid — which is exactly what the
 * written reference has been warning authors about for as long as a plane had to be faked with
 * `polygon`. The user asked for a plane; the figure should show one.
 *
 * A starting point and not a rule: the properties panel restyles it like anything else.
 */
function styleFor(source: EntitySource, pending: Style, context: ToolContext): Style {
  if (!isPlaneSource(source)) return pending;

  /*
   * Counted from the document, so the *second* plane in a figure is told apart from the first.
   * The rule itself lives in the domain — see `toneForPlane` — because the script has to reach
   * the same answer, and a second copy of it here is the shape of mistake that has already put
   * an untoned plane on the page twice.
   */
  const already = [...context.document.entities.values()].filter((entity) =>
    isPlaneSource(entity.source)
  ).length;

  return { ...pending, ...toneForPlane(already) };
}

export const aPlane = (hint = 'một mặt phẳng'): PickStep => ({
  accepts: (kind) => kind === 'plane',
  hint,
});

export const aVector = (hint = 'một vector'): PickStep => ({
  accepts: (kind) => kind === 'vector',
  hint,
});

/**
 * Anything a curve can be intersected with.
 *
 * Deliberately excludes points: two points do not intersect, and offering the pick would
 * only produce a construction that is undefined from birth.
 */
export const aCurve = (hint = 'một đường'): PickStep => ({
  accepts: (kind) =>
    kind === 'line' ||
    kind === 'ray' ||
    kind === 'segment' ||
    kind === 'circle' ||
    kind === 'arc' ||
    kind === 'ellipse',
  hint,
});

/**
 * One side of an angle: the side itself, or a point out along it.
 *
 * Both are accepted because both are natural. Somebody marking the angle of a triangle
 * reaches for the *sides*; somebody working from three named points reaches for the points.
 * Refusing either would make the tool feel broken to half its users — which is exactly what
 * the point-only version did.
 */
export const anArm = (hint = 'cạnh của góc (đoạn thẳng) hoặc một điểm'): PickStep => ({
  accepts: (kind) =>
    kind === 'point' || kind === 'segment' || kind === 'vector' || kind === 'polygon',
  hint,
  resolve: (context, id, world, picked) => {
    const entity = context.document.entities.get(id);
    if (entity?.source.op !== 'polygon') return id;
    return armCornerOfPolygon(context, entity.source.vertices, world, picked);
  },
});

/**
 * The corner a click on a polygon's side means, as a point the construction can name.
 *
 * ## Why this exists at all
 *
 * A triangle drawn with the polygon tool is *one* entity. Its sides are not entities, so
 * there is nothing to pick along one — and the angle tool refused every side of every
 * polygon, in silence, which is exactly what a user reports as "I cannot click the second
 * side". Telling them to click the corner points instead would be answering a reasonable
 * request with a workaround: the side of a triangle is a side, and clicking it should work.
 *
 * ## Why a corner point rather than a synthetic segment
 *
 * A corner is already an entity, so the mark refers to something that survives editing and
 * keeps following the polygon when it is reshaped. A synthetic segment would have to be
 * invented, stored, and kept in step with the polygon by hand.
 *
 * ## Which corner
 *
 * The one whose direction from the vertex agrees with the direction from the vertex to the
 * click. That single rule covers both cases without a special branch: at a corner it picks
 * the far end of the side clicked, and for a vertex sitting *on* a side it picks the end the
 * user reached toward — which is the half of the side they were pointing at.
 */
function armCornerOfPolygon(
  context: ToolContext,
  vertices: readonly EntityId[],
  world: Vec2,
  picked: readonly EntityId[]
): EntityId | null {
  const vertexId = picked[0];
  if (vertexId === undefined) return null;

  const at = context.scene.values.get(vertexId);
  if (at?.kind !== 'point') return null;

  const toward = vec.subtract(world, at.point);
  if (vec.isZero(toward)) return null;

  let best: { id: EntityId; agreement: number } | null = null;
  for (const cornerId of vertices) {
    if (cornerId === vertexId) continue;

    const corner = context.scene.values.get(cornerId);
    if (corner?.kind !== 'point') continue;

    const arm = vec.subtract(corner.point, at.point);
    if (vec.isZero(arm)) continue;

    // Cosine of the angle between the arm and the click. Comparing the *normalised* agreement
    // keeps a distant corner from beating a nearer, better-aligned one purely on length.
    const agreement = vec.dot(vec.normalize(arm), vec.normalize(toward));
    if (best === null || agreement > best.agreement) best = { id: cornerId, agreement };
  }

  // A corner behind the click is not the side that was clicked; refusing beats guessing.
  return best !== null && best.agreement > 0 ? best.id : null;
}

/** A closed region: a constructed polygon, or a closed drawn one. */
export const aRegion = (hint = 'một đa giác'): PickStep => ({
  accepts: (kind) => kind === 'polygon' || kind === 'polyline',
  hint,
});

/** A mirror to reflect in: a linear entity, or a point for a half-turn. */
export const aMirror = (hint = 'trục hoặc tâm đối xứng'): PickStep => ({
  accepts: (kind) => kind === 'line' || kind === 'ray' || kind === 'segment' || kind === 'point',
  hint,
});
